import styled, {css} from 'styled-components'

export const StyledModalHeaderTexts = styled.div`
    ${({theme}) => css`
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: ${theme.spacing * 0.5}px;

        & > small {
            ${theme.typography.textXs};
            color: ${theme.palette.neutral['500']};
        }

        & > p {
            ${theme.typography.textXs};
            color: ${theme.palette.neutral['500']};
        }
    `}
`

export const StyledModalTabs = styled.div`
    ${({theme}) => css`
        display: flex;
        gap: ${theme.spacing}px;
        padding: ${theme.spacing * 3}px ${theme.spacing * 5}px 0;
        border-bottom: 1px solid ${theme.palette.neutral['200']};
        overflow-x: auto;
        flex-shrink: 0;
    `}
`

export const StyledModalTab = styled.button<{$active: boolean}>`
    ${({theme, $active}) => css`
        display: inline-flex;
        align-items: center;
        gap: ${theme.spacing * 1.5}px;
        padding: ${theme.spacing * 2}px ${theme.spacing * 3}px;
        margin-bottom: -1px;
        ${theme.typography.textSm};
        font-weight: 500;
        white-space: nowrap;
        background: transparent;
        border: none;
        border-bottom: 2px solid ${$active ? theme.palette.primary['600'] : 'transparent'};
        color: ${$active ? theme.palette.primary['600'] : theme.palette.neutral['500']};
        cursor: pointer;
        transition: ${theme.transition};

        &:hover {
            color: ${$active ? theme.palette.primary['600'] : theme.palette.neutral['700']};
        }
    `}
`

export const StyledModalTabCounter = styled.span<{$active: boolean}>`
    ${({theme, $active}) => css`
        padding: ${theme.spacing * 0.5}px ${theme.spacing * 1.5}px;
        border-radius: 999px;
        font-size: 10px;
        line-height: 14px;
        background: ${$active ? theme.palette.primary['100'] : theme.palette.neutral['100']};
        color: ${$active ? theme.palette.primary['700'] : theme.palette.neutral['500']};
    `}
`

export const StyledModalTableWrapper = styled.div`
    max-height: 55vh;
    overflow: auto;
`

export const StyledModalTable = styled.table`
    ${({theme}) => css`
        width: 100%;
        min-width: 720px;
        border-collapse: collapse;
        ${theme.typography.textSm};

        & thead {
            position: sticky;
            top: 0;
            background: ${theme.palette.neutral['50']};

            & th {
                padding: ${theme.spacing * 2}px ${theme.spacing * 4}px;
                text-align: left;
                ${theme.typography.textXs};
                font-weight: 500;
                color: ${theme.palette.neutral['500']};

                &.centered {
                    text-align: center;
                }
            }
        }

        & tbody tr {
            border-top: 1px solid ${theme.palette.neutral['100']};

            &:hover {
                background: ${theme.palette.neutral['50']};
            }

            & td {
                padding: ${theme.spacing * 2.5}px ${theme.spacing * 4}px;
                color: ${theme.palette.neutral['700']};
                vertical-align: middle;

                &.centered {
                    text-align: center;
                }

                &.error-cell {
                    ${theme.typography.textXs};
                    color: ${theme.palette.neutral['600']};
                }

                & strong {
                    display: block;
                    font-weight: 500;
                    color: ${theme.palette.neutral['900']};
                }
            }
        }
    `}
`

export const StyledKindBadge = styled.span<{$kind: 'patient' | 'consultant' | 'whitelist'}>`
    ${({theme, $kind}) => css`
        padding: ${theme.spacing * 0.5}px ${theme.spacing * 1.5}px;
        border-radius: 4px;
        font-size: 10px;
        line-height: 14px;
        font-weight: 500;
        background: ${$kind === 'patient'
            ? theme.palette.primary['100']
            : $kind === 'consultant'
              ? theme.palette.purple['100']
              : theme.palette.neutral['100']};
        color: ${$kind === 'patient'
            ? theme.palette.primary['700']
            : $kind === 'consultant'
              ? theme.palette.purple['700']
              : theme.palette.neutral['600']};
    `}
`

export const StyledLogStatusBadge = styled.span<{$status: 'sent' | 'failed' | 'bounced' | 'pending'}>`
    ${({theme, $status}) => css`
        padding: ${theme.spacing * 0.5}px ${theme.spacing * 2}px;
        border-radius: 6px;
        ${theme.typography.textXs};
        font-weight: 500;
        white-space: nowrap;
        background: ${$status === 'sent'
            ? theme.palette.success['50']
            : $status === 'failed'
              ? theme.palette.danger['50']
              : $status === 'bounced'
                ? theme.palette.warning['50']
                : theme.palette.neutral['100']};
        color: ${$status === 'sent'
            ? theme.palette.success['700']
            : $status === 'failed'
              ? theme.palette.danger['700']
              : $status === 'bounced'
                ? theme.palette.warning['700']
                : theme.palette.neutral['600']};
    `}
`

export const StyledModalEmptyState = styled.div`
    ${({theme}) => css`
        padding: ${theme.spacing * 12}px ${theme.spacing * 5}px;
        text-align: center;
        ${theme.typography.textSm};
        color: ${theme.palette.neutral['400']};
    `}
`

export const StyledModalPagination = styled.div`
    ${({theme}) => css`
        display: flex;
        align-items: center;
        gap: ${theme.spacing * 2}px;

        & > span.page-label {
            ${theme.typography.textXs};
            color: ${theme.palette.neutral['500']};
        }

        & > span.results-label {
            ${theme.typography.textXs};
            color: ${theme.palette.neutral['400']};
            margin-left: ${theme.spacing}px;
        }
    `}
`
